/**
 * Pop-out figures.
 *
 * Clicking the expand control (or an active carousel slide) mirrors the image into a
 * body-level overlay. `.picwrap` lives inside `.netzone` (isolation: isolate) and
 * inherits a transform from the reveal layer, so a fixed child of it would be
 * positioned against the wrapper and painted below the sticky header.
 */
export default function popOut() {
    var wraps = document.querySelectorAll('.picwrap');
    var slides = document.querySelectorAll('.carousel .cslide');
    if (!wraps.length && !slides.length) { return; }

    var pop = document.createElement('div');
    pop.className = 'picpop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-modal', 'true');
    pop.hidden = false;

    var shot = document.createElement('img');
    shot.alt = '';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'popclose';
    close.textContent = 'Close';

    pop.append(shot, close);
    document.body.appendChild(pop);

    var opener = null;

    function hide() {
        if (!opener) { return; }
        pop.removeAttribute('data-on');
        var previous = opener;
        opener = null;
        var back = previous.matches('.cslide') ? previous : previous.querySelector('[data-expand]');
        if (back) { back.focus({ preventScroll: true }); }
    }

    close.addEventListener('click', hide);
    pop.addEventListener('click', function (event) {
        if (event.target !== close) { hide(); }
    });

    addEventListener('keydown', function (event) {
        if (!opener) { return; }
        if (event.key === 'Escape') {
            hide();
            return;
        }
        if (event.key === 'Tab') {
            event.preventDefault();
            close.focus({ preventScroll: true });
        }
    });

    function openFrom(source, img, width) {
        opener = source;
        shot.src = img.currentSrc || img.src;
        shot.alt = img.alt || '';
        pop.style.setProperty('--popW', width + 'px');
        pop.setAttribute('data-on', '');
        close.focus({ preventScroll: true });
    }

    for (var i = 0; i < wraps.length; i++) {
        (function (wrap) {
            var figure = wrap.querySelector('.spic--pop');
            var img = figure && figure.querySelector('img');
            var button = wrap.querySelector('[data-expand]');
            if (!figure || !img) { return; }

            function measure() {
                var rest = figure.getBoundingClientRect();
                if (!rest.width) { return; }

                var aspect =
                    img.naturalWidth && img.naturalHeight
                        ? img.naturalWidth / img.naturalHeight
                        : rest.width / rest.height;

                var popWidth = Math.min(
                    1180,
                    innerWidth * 0.92,
                    innerHeight * 0.9 * aspect,
                    img.naturalWidth || Infinity
                );
                var can = popWidth > rest.width * 1.08;

                wrap.style.setProperty('--popW', Math.round(popWidth) + 'px');
                wrap.dataset.popW = Math.round(popWidth);
                wrap.classList.toggle('picwrap--can-pop', can);

                if (button) {
                    button.hidden = !can;
                    button.setAttribute('aria-label', 'Expand image: ' + (img.alt || 'image'));
                }
            }

            measure();
            addEventListener('resize', measure);
            if (!img.complete) { img.addEventListener('load', measure); }
            if (document.fonts && document.fonts.ready) { document.fonts.ready.then(measure); }

            function show() {
                if (!wrap.classList.contains('picwrap--can-pop')) { return; }
                openFrom(wrap, img, wrap.dataset.popW);
            }

            if (button) { button.addEventListener('click', show); }
        })(wraps[i]);
    }

    for (var s = 0; s < slides.length; s++) {
        (function (slide) {
            var img = slide.querySelector('img');
            if (!img) { return; }

            function fits() {
                var rest = (slide.firstElementChild || slide).getBoundingClientRect();
                if (!rest.width) { return 0; }
                var aspect =
                    img.naturalWidth && img.naturalHeight
                        ? img.naturalWidth / img.naturalHeight
                        : rest.width / rest.height;
                var popWidth = Math.min(
                    1180,
                    innerWidth * 0.92,
                    innerHeight * 0.9 * aspect,
                    img.naturalWidth || Infinity
                );
                return popWidth > rest.width * 1.08 ? Math.round(popWidth) : 0;
            }

            function measure() {
                var width = fits();
                slide.classList.toggle('cslide--can-pop', !!width);
                if (width) {
                    slide.dataset.popW = width;
                    slide.setAttribute('role', 'button');
                    slide.setAttribute('tabindex', '0');
                    slide.setAttribute('aria-label', 'Expand image: ' + (img.alt || 'image'));
                } else {
                    delete slide.dataset.popW;
                    slide.removeAttribute('role');
                    slide.removeAttribute('tabindex');
                    slide.removeAttribute('aria-label');
                }
            }

            if (img.loading === 'lazy') { img.loading = 'eager'; }
            measure();
            addEventListener('resize', measure);
            if (!img.complete) { img.addEventListener('load', measure); }
            if (window.MutationObserver) {
                new MutationObserver(measure).observe(slide, {
                    attributes: true,
                    attributeFilter: ['data-on'],
                });
            }

            function open() {
                if (!slide.dataset.popW) { return; }
                openFrom(slide, img, slide.dataset.popW);
            }

            slide.addEventListener('click', open);
            slide.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                }
            });
        })(slides[s]);
    }
}
